import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Dictionary, RedirectItem, TsSnackbarService } from 'tsui';
import { catchError, Observable, of, switchMap, take, tap, throwError, zip } from 'rxjs';
import { Action, createSelector, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { CustomObject, RelationshipType } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerService } from '../services/objects-manager.service';
import { ObjectsManagerActions } from './actions';
import { Account, FieldDef, SavedView } from '@core/models';
import { createFieldDefGroupMetadata, formatRelatedFieldToNestedField } from '@shared/functions';
import { RelatedToType } from '../../../../shared/enums';
import { TsTableOptions } from 'tsui/lib/ts-table/ts-table.model';
import { TsTableHeaderComponent } from 'tsui/lib/ts-table';
import { TableRecordRendererComponent } from '@shared/components/ts-table-renderers/table-record-renderer.component';
import { TableFileUploadEditorComponent } from '@shared/components/ts-table-renderers/editors/table-file-upload-editor.component';
import { AccountState } from '@state/account/state';
import { AccountService } from '@core/services';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project } from 'app/modules/projects/projects.types';
import { FeatureFlag } from '@core/enums';
import { ObjectLink } from '../objects-manager.types';
import UrlUtils from '@shared/Utils/url.utils';
import clone from 'lodash-es/clone';
import { FieldDefsState } from '@state/fields/state';

export interface ObjectsManagerStateModel {
  menuCustomObjects: CustomObject[];
  customObjects: CustomObject[];
  customObject: CustomObject;
  redirectConfig: { [key: string]: RedirectItem };
}

export interface SelectObjectManagerOptions {
  allowedFieldsOnly: boolean;
  addNestedFields: boolean;
}

@State<ObjectsManagerStateModel>({
  name: new StateToken<ObjectsManagerStateModel>('objectsManagerState'),
  defaults: {
    menuCustomObjects: null,
    customObjects: [],
    customObject: null,
    redirectConfig: {},
  },
})
@Injectable()
export class ObjectsManagerState {
  protected accountService = inject(AccountService);

  get account(): Account {
    return this.store.selectSnapshot(AccountState.getAccount) || this.accountService.getCurrentAccount();
  }

  constructor(
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly snackBar: TsSnackbarService,
    private readonly store: Store,
    private readonly projectService: ProjectsService,
  ) {}

  static getLinkedObjects(obj: CustomObject) {
    return createSelector([ObjectsManagerState, AccountState], (state: ObjectsManagerStateModel) => {
      if (!obj) {
        return [];
      }

      return [
        ...state.customObjects.filter((x) => x.links.map((y) => y.linked_type).includes(obj.name)),
        ...state.customObjects
          .filter((y) =>
            obj.links
              .filter((x) => ['many_to_one', 'many_to_many'].includes(x.relationship_type))
              .map((x) => x.linked_type)
              .includes(y.name),
          )
          .filter((x) => x.id),
      ].reduce((p: any[], c: any) => {
        if (p.find((x) => x.name === c.name)) {
          return p;
        }
        return [...p, c];
      }, [] as any[]);
    });
  }

  @Selector()
  static getAllFields(state: ObjectsManagerStateModel): FieldDef[] {
    return state.customObjects.reduce((acc, object) => {
      acc.push(...object.fields);
      return acc;
    }, []);
  }

  @Selector()
  static getForMenu(state: ObjectsManagerStateModel): CustomObject[] {
    return state.menuCustomObjects;
  }

  @Selector()
  static get(state: ObjectsManagerStateModel): CustomObject[] {
    return state.customObjects;
  }

  static getByName(objectName: string) {
    return createSelector([ObjectsManagerState], (state: ObjectsManagerStateModel) => {
      return state.customObjects.find((object) => object.name === objectName);
    });
  }

  static getByUrlPrefix(urlPrefix: string) {
    return createSelector([ObjectsManagerState], (state: ObjectsManagerStateModel) => {
      return state.customObjects.find((object) => object.uiConfig?.urlPrefix === urlPrefix);
    });
  }

  static getUrlPrefixByObjectName(objectName: string) {
    return createSelector([ObjectsManagerState], (state: ObjectsManagerStateModel) => {
      return state.customObjects.find((object) => object.name === objectName).uiConfig.urlPrefix;
    });
  }

  @Selector()
  static getMappedObjects(state: ObjectsManagerStateModel): { [key: string]: CustomObject } {
    return state.customObjects.reduce((acc, object) => {
      acc[object.name] = object;
      return acc;
    }, {});
  }

  @Selector()
  static getById(state: ObjectsManagerStateModel): CustomObject {
    return state.customObject;
  }

  @Selector([ObjectsManagerState.get])
  static getCustomObjectsMap(_: ObjectsManagerStateModel, objects: CustomObject[]): { [key: string]: CustomObject } {
    if (!objects || objects.length === 0) {
      console.error('No objects are available in the state');
      return {};
    }

    return objects
      .filter((obj) => !!obj.id)
      .reduce((acc, obj) => {
        acc[obj.name] = obj;
        return acc;
      }, {});
  }

  static isCustomObject(objectName: string) {
    return createSelector(
      [ObjectsManagerState.getCustomObjectsMap],
      (customObjects: { [key: CustomObject['name']]: CustomObject }) => {
        return !!customObjects[objectName];
      },
    );
  }

  @Selector()
  static getRedirectConfig(state: ObjectsManagerStateModel): { [key: string]: RedirectItem } {
    return state.redirectConfig;
  }

  static getMappedFieldsByObjectName(objectName: string) {
    return createSelector(
      [ObjectsManagerState, ObjectsManagerState.getMappedObjects],
      (state: ObjectsManagerStateModel, tsObject: { [key: string]: CustomObject }): { [key: string]: FieldDef[] } => {
        return tsObject[objectName]?.fields.reduce((acc, field) => {
          acc[field.name] = field;
          return acc;
        }, {});
      },
    );
  }

  static getFieldsByObjectName(
    objectName: string,
    options: SelectObjectManagerOptions = {
      allowedFieldsOnly: false,
      addNestedFields: false,
    },
  ) {
    return createSelector(
      [ObjectsManagerState, ObjectsManagerState.getMappedObjects],
      (state: ObjectsManagerStateModel, mappedObjects: { [key: string]: CustomObject }): FieldDef[] => {
        const tsObject = mappedObjects[objectName];

        if (!tsObject) return [];

        // we need to check hidden fields twice, once for the main object and once for when the nested fields are added
        // we also need to add the group metadata to the fields
        let result = this.processAllowedFields(options.allowedFieldsOnly, tsObject.fields).map((field) => {
          return {
            ...field,
            meta: {
              ...field.meta,
              group: createFieldDefGroupMetadata(tsObject.name, tsObject.label),
            },
          };
        });

        if (objectName === RelatedToType.Trail) {
          result = result.filter(
            (field) => field.meta?.reporting?.hidden === undefined || field.meta?.reporting?.hidden === false,
          );
        }

        if (options.addNestedFields) {
          const fieldsToRelate = result.filter((field: FieldDef) => field.type === 'record');
          // .filter(
          //   (field: FieldDef) =>
          //     field.meta?.filtering?.exclude_in_nested_filter === undefined ||
          //     field.meta?.filtering?.exclude_in_nested_filter === false,
          // );
          const nestedFields = [];

          fieldsToRelate.forEach((field) => {
            const recordType = field?.meta?.record_type;
            const fieldsRelatedToRecordType = mappedObjects[recordType]?.fields;

            const formattedNestedFields = fieldsRelatedToRecordType?.map((relatedField: FieldDef) =>
              formatRelatedFieldToNestedField(relatedField, field),
            );

            if (formattedNestedFields && formattedNestedFields.length > 0) {
              nestedFields.push(...formattedNestedFields);
            }
          });

          result = [...result, ...nestedFields];
        }

        result = result.map((field) => ({
          ...field,
          tsTableOptions: this.getTsTableOption(field),
        }));

        return this.processAllowedFields(options.allowedFieldsOnly, result);
      },
    );
  }

  /**
   * Get fields for an object including timeseries fields from FieldDefsState
   * This composes the existing getFieldsByObjectName with timeseries fields
   */
  static getFieldsByObjectNameWithTimeSeries(
    objectName: string,
    options: SelectObjectManagerOptions = {
      allowedFieldsOnly: false,
      addNestedFields: false,
    },
  ) {
    return createSelector(
      [ObjectsManagerState.getFieldsByObjectName(objectName, options), FieldDefsState.getTimeSeriesFields(objectName)],
      (standardFields: FieldDef[], timeSeriesFields: FieldDef[]): FieldDef[] => {
        // If no timeseries fields, return all standard fields
        if (!timeSeriesFields || timeSeriesFields.length === 0) {
          return standardFields;
        }

        // Extract base field names from timeseries fields
        // Timeseries format: fieldName(cf_name: "fieldName")
        const timeSeriesBaseNames = timeSeriesFields.map((field) => {
          const match = field.name.match(/^(.+)\(cf_name:/);
          return match ? match[1] : field.name;
        });

        // Filter out standard fields that have a timeseries version
        const filteredStandardFields = standardFields.filter((field) => !timeSeriesBaseNames.includes(field.name));

        // Merge filtered standard fields with timeseries fields
        return [...filteredStandardFields, ...timeSeriesFields];
      },
    );
  }

  private static processAllowedFields(allowedFieldsOnly: boolean, result: FieldDef[]): FieldDef[] {
    return allowedFieldsOnly ? result.filter((field: FieldDef) => !field?.archived) : result;
  }

  private static getTsTableOption(field: FieldDef): TsTableOptions {
    const isNestedField = field.name.includes('.');
    const base: TsTableOptions = {
      headerComponentParams: {
        innerHeaderComponent: TsTableHeaderComponent,
        innerHeaderComponentParams: {
          label: isNestedField ? `${field._parentFieldLabel} | ${field.label}` : field.label,
          readonly: field.name === 'predecessor_summary' ? false : field.meta?.readonly || isNestedField,
        },
      },
      headerTooltip: field?.description,
      field: isNestedField ? field._parentFieldName : field.name,
      editable: !isNestedField,
      cellRenderer: isNestedField ? TableRecordRendererComponent : null,
      cellRendererParams: isNestedField
        ? { nestedKeyName: field.name.split('.')[1], recordType: field._parentFieldMeta?.record_type }
        : null,
    };

    // Prefer app-level editor for FileResource records to leverage project services
    if (!isNestedField && field.type === 'record' && field.meta?.record_type === 'FileResource') {
      return {
        ...base,
        cellEditor: TableFileUploadEditorComponent,
        // Optionally pass constraints for accepted types if available in meta
        cellEditorParams: {
          ...(base.cellEditorParams || {}),
          // e.g., field.meta.allowed_types?: string[]
          allowedTypes: (field as any)?.meta?.allowed_types,
        },
      };
    }

    return base;
  }

  @Action(ObjectsManagerActions.GetForMenu)
  getCustomObjectsForMenu(context: StateContext<ObjectsManagerStateModel>): Observable<CustomObject[]> {
    return this.objectsManagerService.get(Number(this.account.id)).pipe(
      tap((response) => {
        const state = context.getState();
        context.setState({ ...state, menuCustomObjects: response.filter(({ id }) => id) });
      }),
    );
  }

  @Action(ObjectsManagerActions.Get)
  getCustomObjects(context: StateContext<ObjectsManagerStateModel>): Observable<CustomObject[]> {
    return this.objectsManagerService.get(Number(this.account.id)).pipe(
      tap((objects) => {
        const state = context.getState();

        context.setState({
          ...state,
          customObjects: objects,
          redirectConfig: this.getRedirectConfig(objects),
        });
      }),
    );
  }

  @Action(ObjectsManagerActions.GetById)
  getCustomObjectById(
    context: StateContext<ObjectsManagerStateModel>,
    { customObjectId }: { customObjectId: number },
  ): Observable<CustomObject> {
    if (isNaN(customObjectId)) {
      return of(context.getState().customObjects.find((x) => x.name === String(customObjectId))).pipe(
        tap((data) => context.patchState({ customObject: data })),
      );
    }

    return this.objectsManagerService.getById(Number(this.account.id), Number(customObjectId)).pipe(
      tap((response) => {
        const state = context.getState();
        context.setState({ ...state, customObject: response });
      }),
    );
  }

  @Action(ObjectsManagerActions.Create)
  createCustomObject(
    context: StateContext<ObjectsManagerStateModel>,
    { customObject }: { customObject: CustomObject },
  ): Observable<CustomObject> {
    const state = context.getState();
    return this.objectsManagerService.create(Number(this.account.id), customObject).pipe(
      switchMap((customObject) =>
        zip(
          of(customObject),
          this.accountService.createSavedView({
            id: null,
            name: `All ${customObject.label_plural}`,
            related_to_type: customObject.name,
          }),
          this.objectsManagerService.get(Number(this.account.id)),
        ),
      ),
      switchMap(([customObject, _, customObjects]: [CustomObject, SavedView, CustomObject[]]) => {
        context.patchState({
          customObjects,
          menuCustomObjects: customObjects.filter(({ id }) => id),
        });
        return of(customObject);
      }),
    );
  }

  @Action(ObjectsManagerActions.SetCustomObject)
  setCustomObject(
    context: StateContext<ObjectsManagerStateModel>,
    { customObject }: { customObject: CustomObject },
  ): void {
    const state = context.getState();
    context.setState({
      ...state,
      customObject,
    });
  }

  @Action(ObjectsManagerActions.Patch)
  patchCustomObject(
    context: StateContext<ObjectsManagerStateModel>,
    { customObjectId, customObject }: { customObjectId: number; customObject: CustomObject },
  ): Observable<CustomObject> {
    const state = context.getState();

    return this.objectsManagerService.patch(Number(this.account.id), customObjectId, customObject).pipe(
      tap((response) => {
        context.setState({ ...state, customObjects: [...state.customObjects, response], customObject: response });
        this.store.dispatch(new ObjectsManagerActions.Get());
      }),
    );
  }
  @Action(ObjectsManagerActions.RemoveLink)
  removeLink(
    context: StateContext<ObjectsManagerStateModel>,
    { link, customObject, force }: { link: ObjectLink; customObject: CustomObject; force: boolean },
  ): Observable<any> {
    return this.objectsManagerService.removeLink(Number(this.account.id), link, force).pipe(
      tap(() => {
        this.store.dispatch(new ObjectsManagerActions.Get());
        this.store.dispatch(
          new ObjectsManagerActions.SetCustomObject({
            ...customObject,
            links: customObject.links.filter((x) => x.id !== link.id),
          }),
        );
      }),
    );
  }

  @Action(ObjectsManagerActions.CreateLink)
  createCustomObjectLink(
    context: StateContext<ObjectsManagerStateModel>,
    {
      link,
    }: {
      link: {
        custom_object_id: number;
        related_to_type: RelatedToType | string;
        linked_type: RelatedToType | string;
        relationship_type: RelationshipType;
      };
    },
  ): Observable<any> {
    return this.objectsManagerService.createLink(Number(this.account.id), link as any).pipe(
      switchMap(() => {
        return this.objectsManagerService.get(Number(this.account.id));
      }),
      tap((customObjects) => {
        context.patchState({
          customObject: {
            ...context.getState().customObject,
            ...(link.custom_object_id
              ? customObjects.find((x) => x.id === link.custom_object_id)
              : customObjects.find((x) => x.name === link.related_to_type)),
          },
          customObjects,
          menuCustomObjects: customObjects.filter(({ id }) => id),
        });
      }),
    );
  }

  @Action(ObjectsManagerActions.Delete)
  deleteCustomObject(
    context: StateContext<ObjectsManagerStateModel>,
    { customObjectId }: { customObjectId: number },
  ): Observable<void> {
    const state = context.getState();

    return this.objectsManagerService.delete(Number(this.account.id), customObjectId).pipe(
      catchError((err: HttpErrorResponse) => {
        this.snackBar.open(err.error?.custom_object?.errors, 'error');
        return throwError(() => err);
      }),
      tap(() => {
        let customObjects = state.customObjects;
        UrlUtils.removeFromLocalStorage(`${customObjects.find((obj) => obj.id === customObjectId)?.name}_PageSize`);
        customObjects = customObjects.filter((v) => v.id !== customObjectId);
        context.setState({ ...state, customObjects });
        this.store.dispatch(new ObjectsManagerActions.GetForMenu());
      }),
    );
  }

  @Action(ObjectsManagerActions.FieldsActions.AddField)
  addField(
    context: StateContext<ObjectsManagerStateModel>,
    { fieldDef }: { fieldDef: FieldDef },
  ): Observable<FieldDef> {
    return this.objectsManagerService.createCustomField(fieldDef).pipe(
      tap((response) => {
        const state = context.getState();
        const customObjects = [...state.customObjects];
        const customObject = customObjects.find(({ name }) => name === response.related_to_type);
        customObject.fields.push(response);
        context.patchState({ customObjects });
      }),
    );
  }

  @Action(ObjectsManagerActions.FieldsActions.UpdateField)
  updateField(
    context: StateContext<ObjectsManagerStateModel>,
    { fieldDef, isCustom }: { fieldDef: FieldDef; isCustom: boolean },
  ): Observable<FieldDef> {
    const method = isCustom ? 'updateCustomField' : 'updateStandardField';
    return this.objectsManagerService[method](fieldDef).pipe(
      tap((response) => {
        const state = context.getState();
        const customObjects = clone(state.customObjects);
        const customObject = customObjects.find(({ name }) => name === response.related_to_type);

        let fieldIndex: number;
        if ((fieldIndex = customObject.fields.findIndex(({ name }) => name === response.name)) > -1) {
          customObject.fields[fieldIndex] = response;
        }

        context.patchState({ customObjects });
      }),
    );
  }

  @Action(ObjectsManagerActions.FieldsActions.AddOrUpdateObjectField)
  addOrUpdateObjectField(context: StateContext<ObjectsManagerStateModel>, { fieldDef }: { fieldDef: FieldDef }): void {
    const state = context.getState();
    const customObjects = [...state.customObjects];
    const customObject = customObjects.find(({ name }) => name === fieldDef.related_to_type);
    let fieldIndex: number;
    if ((fieldIndex = customObject.fields.findIndex(({ name }) => name === fieldDef.name)) > -1) {
      customObject.fields[fieldIndex] = fieldDef;
    } else {
      customObject.fields.push(fieldDef);
    }
    context.patchState({ customObjects });
  }

  private getRedirectConfig(objects: CustomObject[]): { [key: string]: RedirectItem } {
    const isNewCrmOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_CRM));

    return objects
      .filter((x) => ![RelatedToType.User].includes(x.name as RelatedToType))
      .reduce((p, c) => {
        return {
          ...p,
          [c.name]: {
            deferred: c.name === RelatedToType.Project,
            getQueryParams: (key?: number): Dictionary<string> => {
              if (c.id) {
                return { return: `/space/${c.uiConfig.urlPrefix}/0/${key}` };
              }

              if ([RelatedToType.Contact, RelatedToType.Company].includes(c.name as RelatedToType)) {
                if (isNewCrmOn) {
                  return { return: `/space/${c.uiConfig.urlPrefix}/0/${key}` };
                }

                return { return: `/${c.uiConfig.urlPrefix}/${key}` };
              }

              if (c.name === RelatedToType.Property) {
                return { return: `/space/${c.uiConfig.urlPrefix}/0/${key}` };
              }

              return {};
            },
            route: (key?: number): Observable<string> => {
              if (c.id) {
                return of(`/space/void`);
              }

              if ([RelatedToType.Contact, RelatedToType.Company].includes(c.name as RelatedToType)) {
                if (isNewCrmOn) {
                  return of(`/space/void`);
                }

                const obj = this.store.selectSnapshot(ObjectsManagerState.get).find((x) => x.name === c.name);

                return of(`/${obj.apiResponseConfig.api_path}/${key}`);
              }

              if (c.name === RelatedToType.Project) {
                return this.projectService.getProjectByIdBase(String(key)).pipe(
                  take(1),
                  switchMap((project: Project) => of(`/projects/${project.pipeline_id}/${key}/details`)),
                );
              }

              if (c.name === RelatedToType.Property) {
                return of(`/space/void`);
              }

              return of(`/${c.apiResponseConfig.api_path}/:id`);
            },
            routeSnapshot: (key?: number, parent?: number): string => {
              if (c.id) {
                return `/space/void`;
              }

              if (c.name === RelatedToType.Project) {
                return `/projects/${parent}/${key}`;
              }

              if (c.name === RelatedToType.Property) {
                return `/space/void`;
              }

              if ([RelatedToType.Contact, RelatedToType.Company].includes(c.name as RelatedToType)) {
                if (isNewCrmOn) {
                  return `/space/${c.apiResponseConfig.api_path}/0/${key}`;
                }

                return `/${c.apiResponseConfig.api_path}/${key}`;
              }

              return `/${c.apiResponseConfig.api_path}/0/${key}`;
            },
          },
        };
      }, {});
  }
}
